import React from 'react';
import { StarIcon } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useReviews } from '../../../hooks/useReviews';
import { freeShippingLive } from '../../../data/pricing';
import { formatMoney, startingPrice } from '../../../utils/pricing';
import { reviewsAggregate } from '../../../data/reviews';
import { scrollToSection, SHOP_SECTION_ID } from '../../../utils/scroll';
import { REVIEWS_SECTION_ID } from '../../reviews/ReviewSummaryBar';

/**
 * Electric-blue banner attached to the bottom edge of the hero artwork,
 * with holographic silver lettering. The star rating appears only when
 * published reviews exist; otherwise it leads with the real store offers.
 */
export function HoloBanner() {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    summary
  } = useReviews();
  const hasRating = summary.count > 0;
  const fmt = (n: number) => n.toLocaleString(es ? 'es-US' : 'en-US');
  const countLabel = `${fmt(summary.count)}${summary.isAggregate ? '+' : ''}`;
  const lead = hasRating ? `${summary.average.toFixed(1)} ${es ? 'estrellas' : 'stars'} · ${countLabel} ${es ? 'reseñas' : 'reviews'}` : es ? 'Iluminación de grado profesional' : 'Professional-grade lighting';
  const items = [...(hasRating && reviewsAggregate ? [es ? `Hogares iluminados desde ${reviewsAggregate.sinceYear}` : `Lighting homes since ${reviewsAggregate.sinceYear}`] : []), freeShippingLive() ? es ? 'Envío gratis en EE.UU.' : 'Free U.S. shipping' : es ? `Desde ${formatMoney(startingPrice(), lang)}` : `Starting at ${formatMoney(startingPrice(), lang)}`, es ? 'Sin cables. Sin taladrar.' : 'No wires. No drilling.'];
  return <button type="button" onClick={() => scrollToSection(hasRating ? REVIEWS_SECTION_ID : SHOP_SECTION_ID)} className="relative block w-full overflow-hidden border-y border-[#9CD8FF]/60 bg-[#1364D6] px-4 py-3 text-center shadow-[0_6px_24px_rgba(36,141,255,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white md:py-3.5" data-id="element-239" data-mp="3nqjydiuv86w7">
      <span className="mf-holo-sheen" aria-hidden data-id="element-240" data-mp="3np09jjaj1bj" />
      <span className="relative flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-display text-[15px] font-bold uppercase tracking-[0.14em] sm:text-[17px] lg:text-[19px]" data-id="element-241" data-mp="3nypv60fb6gp">
        {hasRating && <span className="inline-flex text-[#E8EEF4] drop-shadow-[0_0_6px_rgba(156,216,255,0.6)]" aria-hidden data-id="element-242" data-mp="3722u61vspy83">
            {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-4 w-4 fill-current sm:h-5 sm:w-5" data-id="element-243" data-mp="3h225u7nii4p6" />)}
          </span>}
        <span className="mf-holo-text" data-id="element-244" data-mp="3lumu6tnnfplb">{lead}</span>
        {items.map((it, i) => <React.Fragment key={it} data-id="element-245" data-mp="2ocmuthvukbt9" data-mpi={i}>
            <span aria-hidden className={`text-[#9CD8FF] ${i > 0 ? 'hidden lg:inline' : 'hidden sm:inline'}`} data-id="element-246" data-mp="2obk172753v9n">
              ◆
            </span>
            <span className={`mf-holo-text ${i === 0 ? 'hidden sm:inline' : 'hidden lg:inline'}`} data-id="element-247" data-mp="2lthzz94ouxrs">{it}</span>
          </React.Fragment>)}
      </span>
    </button>;
}